import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { cleanEmptyParams, createSearchString, parseSearchParams } from "@newLibs/utils";

export function useTableFilters<T extends Record<string, any>>(excludeFilterObjectKeys?: Array<keyof T>) {
  const history = useHistory();
  const location = useLocation();

  const tableFilters = useMemo(() => parseSearchParams(location.search) as T, [location.search]);

  const updateLocationQuery = useCallback(
    (queryObject: Record<string, any>) => {
      const cleaned = cleanEmptyParams(queryObject);
      const queryString = createSearchString(cleaned);

      history.push({
        pathname: location.pathname,
        search: queryString ? `?${queryString}` : "",
      });
    },
    [history, location]
  );

  const updateTableFilters = useCallback(
    (filters: Partial<Record<string, any>>) => {
      const intersection = Object.fromEntries(Object.entries(filters).filter(([key]) => key in filters));
      const difference = Object.fromEntries(Object.entries(tableFilters).filter(([key]) => !(key in filters)));

      const combinedFilters = { ...intersection, ...difference };

      // NOTE: 페이지 직접 이동이 아니면 페이지 파라미터를 제거하여 항상 1페이지가 보이도록 함
      if (combinedFilters.page === tableFilters.page) {
        delete combinedFilters.page;
      }

      updateLocationQuery(combinedFilters);
    },
    [tableFilters, updateLocationQuery]
  );

  const resetTableFilters = useCallback(
    (filters?: Partial<Record<string, any>>) => {
      const difference = Object.fromEntries(Object.entries(tableFilters).filter(([key]) => !(key in (filters ?? {}))));

      delete difference.page;

      updateLocationQuery(difference);
    },
    [tableFilters, updateLocationQuery]
  );

  const hasActiveFilters = useMemo(() => {
    if (excludeFilterObjectKeys) {
      return (
        typeof tableFilters === "object" &&
        Object.keys(tableFilters).filter((filterKey) => !excludeFilterObjectKeys.includes(filterKey)).length > 0
      );
    }

    return typeof tableFilters === "object" && Object.keys(tableFilters).length > 0;
  }, [tableFilters, excludeFilterObjectKeys]);

  return { tableFilters, updateTableFilters, resetTableFilters, hasActiveFilters };
}
